import React from 'react';

import { Modal, Badge, Button, Copy } from '../../../../components';
import { SafeLink } from '../../../../components/Link';
import { CheckIcon } from '../../../../assets/icons';
import { format } from 'date-fns';
import type { TaskDetails } from '../../../../services/scraper';
import type { VisitStatsResponse } from '../../../../api';

interface TaskInfoModalProps {
  task: TaskDetails | null;
  open: boolean;
  onClose: () => void;
  visitStats: VisitStatsResponse | null;
  isLoadingStats: boolean;
}

const TaskInfoModal: React.FC<TaskInfoModalProps> = ({
  task,
  open,
  onClose,
  visitStats,
  isLoadingStats,
}) => {
  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), 'dd MMM yyyy, HH:mm:ss');
  };

  if (!task) return null;

  return (
    <Modal open={open} onClose={onClose} title="Task Information">
      <div className="p-4 space-y-6">
        {/* First Row: Basic Information and Target Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-2xl font-semibold text-mainText mb-4">
              Basic Information
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Task ID:
                </span>
                <span className="text-secondary text-tiny">{task.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Status:
                </span>
                <Badge status={task.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Created:
                </span>
                <span className="text-secondary text-tiny">
                  {task.created_at ? formatDate(task.created_at) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Requested by:
                </span>
                <div className="flex items-center">
                  <span className="text-secondary text-tiny">
                    {task.requested_by || 'Unknown'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Used tokens:
                </span>
                <div className="flex items-center">
                  <span className="text-secondary text-tiny">
                    {task.used_tokens || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-2xl font-semibold text-mainText mb-4">
              Target Information
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Company:
                </span>
                <span className="text-secondary text-tiny">
                  {task.company_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Campaign:
                </span>
                <span className="text-secondary text-tiny">
                  {task.campaign_name || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  URL:
                </span>
                <span className="text-secondary text-tiny break-all">
                  {task.url}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Demo Bot:
                </span>
                <CheckIcon
                  color={task.should_create_demo_bot ? '#5861FA' : '#dbe0e5'}
                  width={20}
                  height={20}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Second Row: Additional Information and Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-2xl font-semibold text-mainText mb-4">
              Additional Information
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Organization ID:
                </span>
                <span className="text-secondary text-tiny">
                  {task.organization_id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Agent ID:
                </span>
                <span className="text-secondary text-tiny">
                  {task.agent_id}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium">
                  Demo URL:
                </span>
                <div className="flex items-center gap-x-2">
                  {task.demo_url ? (
                    <>
                      <SafeLink
                        to={task.demo_url}
                        external
                        newTab
                        className="text-secondary text-tiny break-all hover:text-main transition-colors"
                      >
                        {task.demo_url}
                      </SafeLink>
                      <Copy
                        text={task.demo_url}
                        className="shrink-0"
                        iconClassName="text-medium"
                      />
                    </>
                  ) : (
                    <span className="text-secondary text-tiny">N/A</span>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary text-tiny font-medium pr-sm">
                  Screenshot URL:
                </span>
                <div className="flex items-center gap-x-2">
                  {task.screenshot_url ? (
                    <>
                      <SafeLink
                        to={task.screenshot_url}
                        external
                        newTab
                        className="text-secondary text-tiny break-all hover:text-main transition-colors"
                      >
                        {task.screenshot_url}
                      </SafeLink>
                      <Copy
                        text={task.screenshot_url}
                        className="shrink-0"
                        iconClassName="text-medium"
                      />
                    </>
                  ) : (
                    <span className="text-secondary text-tiny">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-2xl font-semibold text-mainText mb-4">
              Configuration
            </h4>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-secondary text-tiny font-medium">
                    Depth:
                  </span>
                  <span className="text-secondary font-semibold text-small">
                    {task.depth || 'Unlimited'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Maximum crawling depth</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-secondary text-tiny font-medium">
                    Page Limit:
                  </span>
                  <span className="text-secondary font-semibold text-small">
                    {task.page_limit ?? 'N/A'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">Maximum pages to crawl</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-secondary text-tiny font-medium">
                    Include Links:
                  </span>
                  <CheckIcon
                    color={task.is_include_links ? '#5861FA' : '#dbe0e5'}
                    width={20}
                    height={20}
                  />
                </div>
                <p className="text-sm text-gray-500">Include links in output</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-secondary text-tiny font-medium">
                    Screenshot:
                  </span>
                  <CheckIcon
                    color={task.take_screenshot ? '#5861FA' : '#dbe0e5'}
                    width={20}
                    height={20}
                  />
                </div>
                <p className="text-sm text-gray-500">Take website screenshot</p>
              </div>
            </div>
          </div>
        </div>

        {/* Visits Section */}
        {task.demo_url && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-2xl font-semibold text-mainText mb-4">
              Visits
            </h4>
            {isLoadingStats ? (
              <div className="text-secondary text-tiny">Loading...</div>
            ) : visitStats ? (
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-secondary text-tiny font-medium">
                    Short ID:
                  </span>
                  <span className="text-secondary text-tiny">
                    {visitStats.short_id || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary text-tiny font-medium">
                    Total Count:
                  </span>
                  <span className="text-secondary text-tiny">
                    {visitStats.total_count ?? 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary text-tiny font-medium">
                    Last Visited At:
                  </span>
                  <span className="text-secondary text-tiny">
                    {visitStats.last_visited_at
                      ? formatDate(visitStats.last_visited_at)
                      : 'N/A'}
                  </span>
                </div>
                {visitStats.country_stats &&
                Object.keys(visitStats.country_stats).length > 0 ? (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h5 className="text-lg font-semibold text-mainText mb-3">
                      Country Stats
                    </h5>
                    <div className="bg-gray-100 rounded-md p-3 border border-gray-200">
                      <div className="space-y-2">
                        {Object.entries(visitStats.country_stats).map(
                          ([country, count]) => (
                            <div
                              key={country}
                              className="flex justify-between items-center py-1 px-2 hover:bg-gray-50 rounded transition-colors"
                            >
                              <span className="text-secondary text-sm font-medium">
                                {country}:
                              </span>
                              <span className="text-mainText text-sm font-semibold">
                                {count}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <h5 className="text-lg font-semibold text-mainText">
                        Country Stats
                      </h5>
                      <span className="text-secondary text-tiny">N/A</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-secondary text-tiny">
                No visit statistics available
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button
            onClick={onClose}
            label="Close"
            type="contrast"
            size="medium"
            className="px-4 py-2 text-main"
          />
        </div>
      </div>
    </Modal>
  );
};

export default TaskInfoModal;
